/**
 * AL-265: Buying Packages change request.
 * Old (2k/5k/25k/50k @ $50/credit tiered) pricing replaced with the new
 * Starter/Growth/Enterprise structure below.
 *
 * Starter is the baseline rate: $50 per credit. Each larger bundle offers a
 * lower per-credit rate, and the advertised discount is the percentage that
 * rate is below Starter's $50:
 *   Growth     $45/credit → (50 - 45) / 50 = 10% off Starter
 *   Enterprise $40/credit → (50 - 40) / 50 = 20% off Starter
 *
 * Headline prices are the round marketing figures ($5,000 / $10,000 /
 * $25,000). `discountPercent` (not the raw dollar delta) is what the buyer
 * sees, so it reflects the per-credit saving relative to Starter.
 *
 * OFFER is kept in config only for backward compatibility with historical
 * payment records (payments.plan_code may still reference CREDITS_OFFER) —
 * it is intentionally excluded from the buyer-facing bundle selector in
 * TopUpModal.tsx via BUYABLE_PLAN_KEYS.
 */
export const BILLING_PLANS = {
  STARTER: {
    id: 'starter',
    name: 'Starter',
    description: '100 credits · $50/ea',
    type: 'credits',
    credits: 100,
    price: 500000, // $5,000 = 500000 cents (100 × $50)
    originalPrice: 500000,
    pricePerCredit: 5000, // $50 per credit
    originalPricePerCredit: 5000,
    discountPercent: 0,
    savings: 0,
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
    ],
  },
  GROWTH: {
    id: 'growth',
    name: 'Growth',
    description: '222 credits · $45/ea',
    type: 'credits',
    credits: 222,
    price: 1000000, // $10,000 = 1000000 cents (round headline price)
    originalPrice: 1110000, // $11,100 at Starter's $50/credit rate (222 × $50)
    pricePerCredit: 4500, // $45 per credit
    originalPricePerCredit: 5000, // $50 per credit (Starter baseline)
    discountPercent: 10, // $45 is 10% below Starter's $50/credit
    savings: 110000, // $1,100 vs $50/credit baseline
    // GROWTH deliberately carries no badge: ENTERPRISE is the POPULAR one, and
    // badging both reads as a mistake. Restored from main during the EBF merge.
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
      'Priority Support',
    ],
  },
  ENTERPRISE: {
    id: 'enterprise',
    name: 'Enterprise',
    description: '625 credits · $40/ea',
    type: 'credits',
    credits: 625,
    price: 2500000, // $25,000 = 2500000 cents (625 × $40)
    originalPrice: 3125000, // $31,250 at $50/credit rate
    pricePerCredit: 4000, // $40 per credit
    originalPricePerCredit: 5000, // $50 per credit
    discountPercent: 20,
    savings: 625000, // Save $6,250
    badge: 'POPULAR',
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
      'Priority Support',
      'Dedicated Account Manager',
    ],
  },
  // Retired offer (AL-265) — not shown in the buyer-facing selector, kept
  // here only so historical payment records referencing CREDITS_OFFER
  // still resolve to a valid plan definition.
  OFFER: {
    id: 'offer',
    name: 'Offer',
    description: '1,667 credits · $30/ea',
    type: 'credits',
    credits: 1667,
    price: 5000000, // $50,000 = 5000000 cents
    originalPrice: 8335000, // $83,350 at $50/credit rate
    pricePerCredit: 3000, // $30 per credit
    originalPricePerCredit: 5000, // $50 per credit
    discountPercent: 40,
    savings: 3335000, // Save $33,350
    badge: 'LIMITED',
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
      'Priority Support',
      'Dedicated Account Manager',
      'Volume Discount — Lowest Rate',
    ],
  },
} as const;

export type BillingPlanKey = keyof typeof BILLING_PLANS;

/** Plans shown in the buyer-facing bundle selector (AL-265 retired OFFER). */
export const BUYABLE_PLAN_KEYS: BillingPlanKey[] = ['STARTER', 'GROWTH', 'ENTERPRISE'];

/** Australian GST rate applied to top-up prices. */
export const GST_RATE = 0.1;

export interface GstBreakdown {
  /** GST-exclusive price, in cents. */
  subtotalCents: number;
  /** GST component (10% of subtotal), in cents. */
  gstCents: number;
  /** GST-inclusive total, in cents. Always equals subtotal + gst. */
  totalIncGstCents: number;
}

/**
 * Compute the GST breakdown for a GST-exclusive price, matching the signup
 * flow (prices exclude GST; 10% GST is added). The total is the sum of the
 * subtotal and GST so the three figures are always self-consistent — the
 * backend TaxInvoice rejects breakdowns that do not add up.
 */
export function getGstBreakdown(priceExGstCents: number): GstBreakdown {
  const gstCents = Math.round(priceExGstCents * GST_RATE);
  return {
    subtotalCents: priceExGstCents,
    gstCents,
    totalIncGstCents: priceExGstCents + gstCents,
  };
}
